import { Component, ElementRef, OnChanges, type SimpleChanges, Input, ViewChild, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { FormsModule } from '@angular/forms';
import { SelectInput } from '../../../../../../utils/models/input.interface';
import { buildXAxisDataZoom, CHART_GRID_BOTTOM_ZOOM_ONLY } from 'src/app/shared/components/charts/chart-zoom.util';
import dayjs from 'dayjs';
import ruptureModelsJson from './rupture-models.json';

/** Origem de uma linha do tooltip. O rótulo vem do artefato; o valor é resolvido no frontend. */
export type TemporalChartTooltipSource = 'x' | 'series.key' | 'column' | 'series.attribute';

export interface TemporalChartTooltipField {
  from: TemporalChartTooltipSource;
  label: string;
  /** Coluna em series.attributes quando from é series.attribute. */
  column?: string;
}

export interface TemporalChartConfig {
  type?: string;
  mode?: string;
  legend?: { show?: boolean };
  dateFormat?: string;
  axisPointer?: { type?: string };
  tooltip?: {
    trigger?: string;
    fields?: TemporalChartTooltipField[];
  };
}

/** Uma linha ECharts alinhada ao eixo x. attributes[coluna][i] acompanha x[i]; null quando y[i] é null. */
export interface TemporalChartSeries {
  key: string;
  y: Array<number | null>;
  attributes?: Record<string, Array<string | null>>;
}

export interface TemporalDataItem {
  id: string;
  table_id: string;
  variable_name: string;
  x: string[];
  y: number[];
  unit?: string;
  column: string;
  chart?: TemporalChartConfig;
  series?: TemporalChartSeries[];
}

/** Marcador de ponto que passou por tratamento no pipeline (ex.: preenchimento de faltante) */
export interface DataTreatmentAnnotation {
  x: string;
  y: number;
  treatmentType: string;
  description: string;
}

/** Região destacada no gráfico (blockspot) */
export interface BlockspotAnnotation {
  startX: string;
  endX: string;
  label: string;
  description: string;
  severity?: 'info' | 'warning' | 'critical';
}

/** Estrutura do mock de anotações (para uso futuro com backend) */
export interface ChartAnnotationsMock {
  treatmentMarkers: Partial<DataTreatmentAnnotation>[];
  blockspots: Partial<BlockspotAnnotation>[];
}

export interface RuptureGroup {
  group_name: string;
  startEndIndex: string;
}
export interface RuptureModelsJson {
  [key: string]: RuptureModel;
}

export interface RuptureModel {
  breakPoints: number[];
  groups: RuptureGroup[];
}

export interface CorrelationData {
  id: string;
  table_id: string;
  correlations: Array<Record<string, number>>;
  created_at: string;
  updated_at: string;
}

export interface CorrelationStats {
  negative: number;
  weak: number;
  positive: number;
  strong: number;
}

const MULTI_SERIES_MODE = 'multi_series';
const DAY_MONTH_YEAR_FORMAT = 'DD/MM/YYYY';

/** Paleta cíclica; a primeira cor é a da série única legada. A legenda não lista as séries. */
const MULTI_SERIES_LINE_COLORS: readonly string[] = [
  '#0074C0',
  '#E74C3C',
  '#4CAF50',
  '#F39C12',
  '#9B59B6',
  '#3498DB',
  '#1ABC9C',
  '#E67E22',
  '#8E44AD',
  '#2C3E50',
];

interface AxisTooltipParam {
  dataIndex?: number;
}

/** Estilos por tipo de tratamento de dado */
const TREATMENT_STYLES: Record<string, { color: string; symbol: string; label: string }> = {
  mean_fill: { color: '#F39C12', symbol: 'circle', label: 'Preenchido com média' },
  max_fill: { color: '#E74C3C', symbol: 'circle', label: 'Preenchido com máximo' },
  min_fill: { color: '#3498DB', symbol: 'circle', label: 'Preenchido com mínimo' },
  zero_fill: { color: '#95A5A6', symbol: 'circle', label: 'Preenchido com zero' },
  interpolation: { color: '#9B59B6', symbol: 'circle', label: 'Interpolado' },
};

@Component({
  selector: 'advanced-analysis',
  standalone: true,
  imports: [
    CommonModule,
    NgxEchartsModule,
    FormsModule
  ],
  templateUrl: './advanced-analysis.component.html',
  styleUrls: ['./advanced-analysis.component.scss']
})
export class AdvancedAnalysisComponent implements OnChanges {
  @Input() temporalData: TemporalDataItem = null;
  @Input() rupturesData: RuptureModelsJson = {};
  @ViewChild('analysisChart', { read: ElementRef })
  private analysisChart?: ElementRef<HTMLElement>;

  selectedModel: string | null = null;

  modelSelectInput: SelectInput = {
    placeholder: 'Nenhum modelo selecionado',
    label: 'Selecione o modelo:',
    value: '',
    options: []
  };

  /** Toggles de visibilidade das anotações (alertas / linhas de referência) */
  annotationToggles = signal({
    thresholds: true,
    alertPoints: true,
    treatmentMarkers: true,
    blockspots: true,
    nullPoints: false,
  });

  chartOptions: any = {};
  legendItems = signal<Array<{ label: string; backgroundColor: string; borderColor: string; icon: string; hidden: boolean; ignoreHidden: boolean }>>([]);
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['temporalData']) {
      this.updateChart();
    }

    if (changes['rupturesData'] && this.rupturesData) {
      this.initializeModelSelectInput();
    }
  }

  private initializeModelSelectInput(): void {
    this.modelSelectInput.options = [];
    Object.keys(this.rupturesData).forEach(key => {
      this.modelSelectInput.options.push({
        value: key,
        viewValue: key
      });
    });
  }

  onModelChange(event: any): void {
    if (event && event.option !== undefined) {
      this.selectedModel = event.option || null;
      this.modelSelectInput.value = event.option || '';
      this.updateChart();
    }
  }

  toggleAnnotation(key: 'thresholds' | 'alertPoints' | 'treatmentMarkers' | 'blockspots' | 'nullPoints'): void {
    this.annotationToggles.update((t) => ({ ...t, [key]: !t[key] }));
    this.updateChart();
  }

  hasNullData(): boolean {
    return this.temporalData?.y?.some((v) => this.isMissingValue(v)) ?? false;
  }

  /** Gera anotações mock a partir dos dados temporais atuais (para demonstração até o backend enviar) */
  private buildMockAnnotationsFromTemporalData(): {
    treatmentMarkers: DataTreatmentAnnotation[];
    blockspots: BlockspotAnnotation[];
  } {
    if (!this.temporalData || this.temporalData.x.length === 0) {
      return { treatmentMarkers: [], blockspots: [] };
    }
    const x = this.temporalData.x;
    const y = this.temporalData.y;
    const len = x.length;
    const treatmentTypes = Object.keys(TREATMENT_STYLES);

    const indices = [
      Math.floor(len * 0.08),
      Math.floor(len * 0.22),
      Math.floor(len * 0.38),
      Math.floor(len * 0.55),
      Math.floor(len * 0.78),
    ].filter((i) => i >= 0 && i < len);

    const treatmentMarkers: DataTreatmentAnnotation[] = indices.map((i, idx) => ({
      x: x[i],
      y: y[i],
      treatmentType: treatmentTypes[idx % treatmentTypes.length],
      description: `Dado tratado pelo pipeline (mock) - ${TREATMENT_STYLES[treatmentTypes[idx % treatmentTypes.length]]?.label ?? treatmentTypes[idx % treatmentTypes.length]}`,
    }));

    const start1 = Math.floor(len * 0.12);
    const end1 = Math.floor(len * 0.22);
    const start2 = Math.floor(len * 0.52);
    const end2 = Math.floor(len * 0.65);

    const blockspots: BlockspotAnnotation[] = [];
    if (start1 < end1 && end1 <= len) {
      // blockspots.push({
      //   startX: x[start1],
      //   endX: x[end1],
      //   label: 'Gap de dados',
      //   description: 'Período com dados faltantes tratados pelo pipeline (mock)',
      //   severity: 'warning',
      // });
    }

    return { treatmentMarkers, blockspots };
  }

  private updateChart(): void {
    if (!this.temporalData) {
      this.chartOptions = {};
      return;
    }

    if (this.temporalData.chart?.mode === MULTI_SERIES_MODE) {
      this.updateMultiSeriesChart();
      return;
    }

    // Valores válidos (não nulos) para estatísticas
    const validY = this.temporalData.y.filter((v): v is number => v != null && !Number.isNaN(v));
    const meanY = validY.length > 0 ? validY.reduce((a, b) => a + b, 0) / validY.length : 0;

    // Eixo categórico: zoom por índice evita espaços em branco nas bordas (comum em eixo time)
    const categories = this.temporalData.x.map((date) => this.formatAxisDate(date));
    // null quebra a linha (sem interpolar gaps reais)
    const mainSeriesData = this.temporalData.y.map((value) =>
      value == null || Number.isNaN(value) ? null : value
    );

    const series: any[] = [{
      name: '',
      type: 'line',
      smooth: true,
      connectNulls: false,
      showSymbol: false,
      symbol: 'circle',
      symbolSize: 6,
      color: '#0074C0',
      lineStyle: { width: 3, color: '#0074C0' },
      // Evita decal automático do aria na série principal
      itemStyle: { color: '#0074C0', decal: { symbol: 'none' } },
      data: mainSeriesData,
      z: 10
    }];

    const toggles = this.annotationToggles();
    const hasNulls = this.hasNullData();

    // Adiciona break points e grupos se um modelo está selecionado
    if (this.selectedModel && this.rupturesData[this.selectedModel]) {
      this.addRupturesToChart(categories, series);
    }

    // Faixas verticais discretas nos gaps (anotação de eixo, não ponto da série)
    if (toggles.nullPoints && hasNulls) {
      this.addNullGapAnnotations(categories, series);
    }

    // const toggles = this.annotationToggles();
    const minY = validY.length > 0 ? Math.min(...validY) : 0;
    const maxY = validY.length > 0 ? Math.max(...validY) : 0;

    // Linhas de referência (threshold): máx, mín, média
    // if (toggles.thresholds) {
    //   const thresholdLines: any[] = [
    //     {
    //       name: 'Máximo',
    //       yAxis: maxY,
    //       lineStyle: { color: '#E74C3C', type: 'dashed', width: 1.5 },
    //       label: {
    //         show: true,
    //         position: 'insideEndTop',
    //         formatter: `Máx: ${this.formatNumber(maxY)}`,
    //         color: '#E74C3C',
    //         fontSize: 11,
    //         backgroundColor: 'rgba(231,76,60,0.08)',
    //         padding: [2, 6],
    //         borderRadius: 3,
    //       },
    //     },
    //     {
    //       name: 'Mínimo',
    //       yAxis: minY,
    //       lineStyle: { color: '#3498DB', type: 'dashed', width: 1.5 },
    //       label: {
    //         show: true,
    //         position: 'insideEndBottom',
    //         formatter: `Mín: ${this.formatNumber(minY)}`,
    //         color: '#3498DB',
    //         fontSize: 11,
    //         backgroundColor: 'rgba(52,152,219,0.08)',
    //         padding: [2, 6],
    //         borderRadius: 3,
    //       },
    //     },
    //     {
    //       name: 'Média',
    //       yAxis: meanY,
    //       lineStyle: { color: '#F39C12', type: 'dotted', width: 1.5 },
    //       label: {
    //         show: true,
    //         position: 'insideEndTop',
    //         formatter: `Média: ${this.formatNumber(meanY)}`,
    //         color: '#F39C12',
    //         fontSize: 11,
    //         backgroundColor: 'rgba(243,156,18,0.08)',
    //         padding: [2, 6],
    //         borderRadius: 3,
    //       },
    //     },
    //   ];
    //   if (!series[0].markLine) {
    //     series[0].markLine = { data: [], symbol: 'none' };
    //   }
    //   series[0].markLine.data = [...(series[0].markLine.data || []), ...thresholdLines];
    // }

    // Balões de alerta: valor máximo e mínimo
    // if (toggles.alertPoints) {
    //   series[0].markPoint = {
    //     symbol: 'pin',
    //     symbolSize: 40,
    //     data: [
    //       {
    //         type: 'max',
    //         name: 'Valor Máximo',
    //         itemStyle: { color: '#E74C3C' },
    //         label: { formatter: 'MÁX', color: '#fff', fontSize: 10, fontWeight: 'bold' },
    //       },
    //       {
    //         type: 'min',
    //         name: 'Valor Mínimo',
    //         itemStyle: { color: '#3498DB' },
    //         label: { formatter: 'MÍN', color: '#fff', fontSize: 10, fontWeight: 'bold' },
    //       },
    //     ],
    //   };
    // }

    // Blockspots: regiões destacadas (mock)
    // if (toggles.blockspots) {
    //   const { blockspots } = this.buildMockAnnotationsFromTemporalData();
    //   const blockspotAreas = blockspots.map((b) => [
    //     {
    //       xAxis: new Date(b.startX).getTime(),
    //       itemStyle: {
    //         color: b.severity === 'warning' ? 'rgba(231, 76, 60, 0.08)' : 'rgba(52, 152, 219, 0.08)',
    //         borderColor: b.severity === 'warning' ? 'rgba(231, 76, 60, 0.35)' : 'rgba(52, 152, 219, 0.35)',
    //         borderWidth: 1,
    //         borderType: 'dashed',
    //       },
    //       label: {
    //         show: true,
    //         position: 'insideTop',
    //         formatter: b.label,
    //         color: b.severity === 'warning' ? '#E74C3C' : '#3498DB',
    //         fontSize: 11,
    //         fontWeight: 'bold',
    //         backgroundColor: 'rgba(255,255,255,0.9)',
    //         padding: [3, 8],
    //         borderRadius: 4,
    //       },
    //     },
    //     { xAxis: new Date(b.endX).getTime() },
    //   ]);
    //   if (!series[0].markArea) {
    //     series[0].markArea = { data: [], silent: false };
    //   }
    //   series[0].markArea.data = [...(series[0].markArea.data || []), ...blockspotAreas];
    // }

    // Série scatter: pontos de tratamento de dados (mock)
    // if (toggles.treatmentMarkers) {
    //   const { treatmentMarkers } = this.buildMockAnnotationsFromTemporalData();
    //   const scatterData = treatmentMarkers.map((ann) => {
    //     const style = TREATMENT_STYLES[ann.treatmentType] || { color: '#666', symbol: 'circle', label: ann.treatmentType };
    //     return {
    //       value: [new Date(ann.x).getTime(), ann.y],
    //       itemStyle: { color: style.color },
    //       symbol: style.symbol,
    //       _annotation: ann,
    //     };
    //   });
    //   series.push({
    //     name: 'Dados tratados',
    //     type: 'scatter',
    //     symbolSize: 12,
    //     z: 20,
    //     data: scatterData,
    //     tooltip: {
    //       trigger: 'item',
    //       formatter: (params: any) => {
    //         const ann = params.data?._annotation as DataTreatmentAnnotation;
    //         if (!ann) return '';
    //         const style = TREATMENT_STYLES[ann.treatmentType] || { color: '#666', label: ann.treatmentType };
    //         return `
    //           <div style="padding:8px 10px;">
    //             <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
    //               <span style="width:10px;height:10px;border-radius:50%;background:${style.color};display:inline-block;"></span>
    //               <b style="font-size:13px;">${style.label}</b>
    //             </div>
    //             <div style="color:#666;font-size:12px;">${ann.description}</div>
    //             <div style="margin-top:4px;font-size:12px;">Valor: <b>${this.formatNumber(ann.y)}</b></div>
    //           </div>`;
    //       },
    //     },
    //   });
    // }

    this.generateLegendItems();

    const pointCount = categories.length;

    this.chartOptions = {
      // Necessário para o ECharts renderizar `itemStyle.decal` nas faixas
      aria: toggles.nullPoints && hasNulls
        ? { enabled: true, decal: { show: true } }
        : { enabled: false },
      grid: { left: 30, right: 5, top: 24, bottom: CHART_GRID_BOTTOM_ZOOM_ONLY },
      dataZoom: buildXAxisDataZoom(pointCount, pointCount),
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line' },
        formatter: (params: any[]) => {
          if (!params || params.length === 0) return '';

          const xAxisValue = params[0].axisValueLabel ?? params[0].axisValue ?? '';
          const dataIndex = params[0].dataIndex as number;
          const rawValue = this.temporalData.y[dataIndex];

          if (rawValue == null || Number.isNaN(rawValue)) {
            return `
              <div style="padding:6px 8px">
                <span style="color:#666; font-size:12px;">${xAxisValue}</span>
                <br/>
                <span style="color:#757575; font-size:13px; display:inline-block; margin-top:4px;">
                  Sem dados nesse período
                </span>
              </div>`;
          }

          // Filtra apenas a série principal (linha do gráfico, name vazio)
          const mainSeries = params.find(p => p.seriesType === 'line' && p.seriesName === '');
          const value = mainSeries
            ? (Array.isArray(mainSeries.data) ? mainSeries.data[1] : mainSeries.data)
            : rawValue;

          const formatted = this.formatNumber(value, true);
          const variableName = this.temporalData.column;
          return `
            <div style="padding:6px 8px">
              <span style="color:#666; font-size:12px;">${xAxisValue}</span>
              <br/>
              <div style="display:flex;flex-direction:column;gap:5px;align-items:flex-start;margin-top:4px;">
                <span style="font-size:14px;font-weight:400;>
                <span style="color:#000;">
                    ${variableName}:
                    <b>${formatted}</b>
                  </span>
                </span>
              </div>
            </div>`;
        }
      },
      legend: {
        show: false
      },
      xAxis: {
        type: 'category',
        data: categories,
        boundaryGap: false,
        axisTick: { alignWithLabel: true },
        axisLine: { show: true },
        axisLabel: {
          interval: 'auto',
          hideOverlap: true,
          margin: 8,
          fontSize: 10,
        },
        splitLine: { show: false }
      },
      yAxis: {
        type: 'value',
        axisLine: { show: true },
        axisLabel: {
          formatter: (val: number) => this.formatNumber(val)
        },
        name: this.temporalData.unit || '',
        nameLocation: 'end',
        nameTextStyle: {
          align: 'right',
          padding: [0, 8, 0, 12],
        },
        splitLine: { show: true, lineStyle: { color: '#E5E5E5' } },
      },
      series: series
    };
  }

  /**
   * N linhas a partir de series[].y, alinhadas a x.
   * A legenda fica oculta: cada date_predict não deve virar um item.
   */
  private updateMultiSeriesChart(): void {
    const data = this.temporalData;
    if (!data) {
      this.chartOptions = {};
      return;
    }

    const categories = (data.x ?? []).map((date) =>
      this.usesDayMonthYearAxis(data) ? this.formatAxisDate(date) : date
    );
    const lineSeries = (data.series ?? []).map((seriesItem, index) => {
      const color = this.multiSeriesColor(index);
      const lineData = categories.map((_, dataIndex) => this.seriesValueAt(seriesItem, dataIndex));

      return {
        name: seriesItem.key,
        type: 'line' as const,
        smooth: true,
        connectNulls: false,
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 6,
        color,
        lineStyle: { width: 3, color },
        itemStyle: { color, decal: { symbol: 'none' } },
        data: lineData,
        z: 10,
      };
    });

    this.legendItems.set([]);

    const pointCount = categories.length;
    this.chartOptions = {
      aria: { enabled: false },
      grid: { left: 30, right: 5, top: 24, bottom: CHART_GRID_BOTTOM_ZOOM_ONLY },
      dataZoom: buildXAxisDataZoom(pointCount, pointCount),
      tooltip: {
        trigger: data.chart?.tooltip?.trigger ?? 'axis',
        axisPointer: { type: data.chart?.axisPointer?.type ?? 'line' },
        // O cartão do gráfico tem altura fixa e corta o hover quando há vários horizontes.
        appendToBody: true,
        enterable: true,
        extraCssText: 'max-height: calc(100vh - 16px); overflow-y: auto; z-index: 9999;',
        position: (point, _params, _dom, _rect, size) =>
          this.multiSeriesTooltipPosition(point, size),
        formatter: (params: AxisTooltipParam | AxisTooltipParam[]) => {
          const first = Array.isArray(params) ? params[0] : params;
          return this.formatMultiSeriesTooltip(first?.dataIndex ?? 0);
        },
      },
      legend: { show: false },
      xAxis: {
        type: 'category',
        data: categories,
        boundaryGap: false,
        axisTick: { alignWithLabel: true },
        axisLine: { show: true },
        axisLabel: {
          interval: 'auto',
          hideOverlap: true,
          margin: 8,
          fontSize: 10,
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: true },
        axisLabel: {
          formatter: (val: number) => this.formatNumber(val),
        },
        name: data.unit || '',
        nameLocation: 'end',
        nameTextStyle: {
          align: 'right',
          padding: [0, 8, 0, 12],
        },
        splitLine: { show: true, lineStyle: { color: '#E5E5E5' } },
      },
      series: lineSeries,
    };
  }

  private usesDayMonthYearAxis(data: TemporalDataItem): boolean {
    return data.chart?.mode === MULTI_SERIES_MODE || data.chart?.dateFormat === DAY_MONTH_YEAR_FORMAT;
  }

  private multiSeriesColor(index: number): string {
    return MULTI_SERIES_LINE_COLORS[index % MULTI_SERIES_LINE_COLORS.length];
  }

  /** null quebra a linha; índice fora de y também é ausência. */
  private seriesValueAt(seriesItem: TemporalChartSeries, dataIndex: number): number | null {
    const value = seriesItem.y?.[dataIndex];
    if (value == null || Number.isNaN(value)) {
      return null;
    }
    return value;
  }

  /**
   * Mantém o tooltip dentro da janela. Sem isso ele sobe e fica atrás da barra de favoritos.
   * Coordenadas do retorno são relativas ao gráfico; o ECharts converte para a página.
   */
  private multiSeriesTooltipPosition(
    point: number[],
    size: { contentSize: number[]; viewSize: number[] },
  ): number[] {
    const gap = 16;
    const viewportMargin = 8;
    const [mouseX, mouseY] = point;
    const tipWidth = size.contentSize[0];
    const tipHeight = size.contentSize[1];
    const viewWidth = size.viewSize[0];
    const viewHeight = size.viewSize[1];

    let x = mouseX + gap;
    if (x + tipWidth > viewWidth) {
      x = mouseX - tipWidth - gap;
    }
    x = Math.max(0, Math.min(x, Math.max(0, viewWidth - tipWidth)));

    let y = mouseY + gap;
    if (y + tipHeight > viewHeight) {
      y = mouseY - tipHeight - gap;
    }

    const chartRect = this.analysisChart?.nativeElement.getBoundingClientRect();
    if (chartRect) {
      const minTop = viewportMargin;
      const maxBottom = window.innerHeight - viewportMargin;
      let top = chartRect.top + y;
      if (top < minTop) {
        y += minTop - top;
        top = minTop;
      }
      if (top + tipHeight > maxBottom) {
        y -= top + tipHeight - maxBottom;
      }
      if (chartRect.top + y < minTop) {
        y = minTop - chartRect.top;
      }
    }

    return [x, y];
  }

  private formatMultiSeriesTooltip(dataIndex: number): string {
    const data = this.temporalData;
    if (!data) {
      return '';
    }

    const fields = data.chart?.tooltip?.fields ?? [];
    // x e column não mudam entre as linhas do mesmo hover.
    const sharedFields = fields.filter((field) => this.isSharedTooltipField(field));
    const seriesFields = fields.filter((field) => !this.isSharedTooltipField(field));
    const blocks: string[] = [];

    (data.series ?? []).forEach((seriesItem, seriesIndex) => {
      const value = this.seriesValueAt(seriesItem, dataIndex);
      if (value == null) {
        return;
      }

      const fieldLines = this.tooltipFieldLines(seriesFields, data, seriesItem, dataIndex);
      const color = this.multiSeriesColor(seriesIndex);
      const separator = blocks.length > 0
        ? 'padding-top:8px;margin-top:8px;border-top:1px solid #E5E5E5;'
        : '';
      blocks.push(`
        <div style="display:flex;flex-direction:column;gap:4px;align-items:flex-start;${separator}">
          <span style="color:#000;font-size:14px;">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px;"></span>
            <b>${this.escapeTooltipText(this.formatNumber(value, true))}</b>
          </span>
          ${fieldLines.join('')}
        </div>`);
    });

    if (blocks.length === 0) {
      const xLabel = this.formatTooltipDate(data.x?.[dataIndex]) ?? '';
      return `
        <div style="padding:6px 8px">
          <span style="color:#666; font-size:12px;">${this.escapeTooltipText(xLabel)}</span>
          <br/>
          <span style="color:#757575; font-size:13px; display:inline-block; margin-top:4px;">
            Sem dados nesse período
          </span>
        </div>`;
    }

    const headerSource = (data.series ?? []).find((seriesItem) => this.seriesValueAt(seriesItem, dataIndex) != null);
    const headerLines = headerSource
      ? this.tooltipFieldLines(sharedFields, data, headerSource, dataIndex)
      : [];
    const header = headerLines.length > 0
      ? `<div style="display:flex;flex-direction:column;gap:2px;align-items:flex-start;margin-bottom:8px;padding-bottom:8px;border-bottom:1px solid #E5E5E5;">${headerLines.join('')}</div>`
      : '';

    return `<div style="padding:6px 8px">${header}${blocks.join('')}</div>`;
  }

  /** Data do eixo e maturidade valem para todas as linhas daquele ponto. */
  private isSharedTooltipField(field: TemporalChartTooltipField): boolean {
    return field.from === 'x' || field.from === 'column';
  }

  private tooltipFieldLines(
    fields: TemporalChartTooltipField[],
    data: TemporalDataItem,
    seriesItem: TemporalChartSeries,
    dataIndex: number,
  ): string[] {
    return fields
      .map((field) => {
        const resolved = this.resolveTooltipField(field, data, seriesItem, dataIndex);
        if (resolved == null) {
          return '';
        }
        return `<span style="color:#666;font-size:12px;">${this.escapeTooltipText(field.label)}: <b>${this.escapeTooltipText(resolved)}</b></span>`;
      })
      .filter((line) => line.length > 0);
  }

  private resolveTooltipField(
    field: TemporalChartTooltipField,
    data: TemporalDataItem,
    seriesItem: TemporalChartSeries,
    dataIndex: number,
  ): string | null {
    switch (field.from) {
      case 'x':
        return this.formatTooltipDate(data.x?.[dataIndex]);
      case 'series.key':
        return this.formatTooltipDate(seriesItem.key);
      case 'column':
        return data.column ?? null;
      case 'series.attribute': {
        const column = field.column;
        if (!column) {
          return null;
        }
        const value = seriesItem.attributes?.[column]?.[dataIndex];
        return value == null || value === '' ? null : value;
      }
      default:
        return null;
    }
  }

  private formatTooltipDate(value: string | undefined): string | null {
    if (!value) {
      return null;
    }
    const parsed = dayjs(value);
    if (!parsed.isValid()) {
      return value;
    }
    return this.formatAxisDate(value);
  }

  private escapeTooltipText(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private formatAxisDate(date: string): string {
    return dayjs(date).locale('pt-br').add(3, 'hours').format(DAY_MONTH_YEAR_FORMAT);
  }

  private isMissingValue(value: number | null | undefined): boolean {
    return value == null || Number.isNaN(value);
  }

  /** Agrupa índices consecutivos sem dado em faixas para markArea */
  private buildNullGapRanges(): Array<{ start: number; end: number }> {
    const values = this.temporalData.y;
    const ranges: Array<{ start: number; end: number }> = [];
    let start: number | null = null;

    for (let i = 0; i <= values.length; i++) {
      const isMissing = i < values.length && this.isMissingValue(values[i]);

      if (isMissing && start === null) {
        start = i;
      }

      if (!isMissing && start !== null) {
        ranges.push({ start, end: i - 1 });
        start = null;
      }
    }

    return ranges;
  }

  /** Faixas verticais hachuradas nos gaps — anotação de eixo, não ponto da série */
  private addNullGapAnnotations(categories: string[], series: any[]): void {
    const ranges = this.buildNullGapRanges();
    if (ranges.length === 0) {
      return;
    }

    // Com muitos pontos, 1 categoria tem poucos pixels: gaps curtos usam markLine
    // (sempre visível); gaps longos mantêm markArea hachurada.
    const SHORT_GAP_MAX_POINTS = 3;
    const gapItemStyle = {
      color: 'rgba(120, 124, 132, 0.18)',
      decal: {
        symbol: 'rect',
        symbolSize: [1, 7],
        dashArrayX: [1, 0],
        dashArrayY: [3, 2],
        rotation: Math.PI / 4,
        color: 'rgba(90, 96, 105, 0.42)',
      },
    };

    const nullAreas: any[] = [];
    const nullMarkLines: any[] = [];

    for (const { start, end } of ranges) {
      const pointCount = end - start + 1;

      if (pointCount <= SHORT_GAP_MAX_POINTS) {
        for (let i = start; i <= end; i++) {
          nullMarkLines.push({
            xAxis: categories[i],
            lineStyle: {
              type: 'solid',
              color: 'rgba(90, 96, 105, 0.28)',
              width: 2,
            },
            label: { show: false },
          });
        }
        continue;
      }

      nullAreas.push([
        {
          xAxis: Math.max(-0.45, start - 0.45),
          itemStyle: gapItemStyle,
        },
        {
          xAxis: Math.min(categories.length - 0.55, end + 0.45),
        },
      ]);
    }

    if (nullAreas.length > 0) {
      const existingAreas = series[0].markArea?.data ?? [];
      series[0].markArea = {
        silent: true,
        animation: false,
        z: 1,
        data: [...existingAreas, ...nullAreas],
      };
    }

    if (nullMarkLines.length > 0) {
      const existingLines = series[0].markLine?.data ?? [];
      series[0].markLine = {
        silent: true,
        animation: false,
        symbol: 'none',
        z: 2,
        lineStyle: {
          type: 'solid',
          color: 'rgba(90, 96, 105, 0.28)',
          width: 2,
        },
        data: [...existingLines, ...nullMarkLines],
      };
    }
  }

  private addRupturesToChart(categories: string[], series: any[]): void {
    const ruptureModel = this.rupturesData[this.selectedModel!];
    if (!ruptureModel) return;

    // Adiciona break points como markLine na série principal
    const markLines = ruptureModel.breakPoints
      .filter(breakIndex => {
        return breakIndex < categories.length;
      })
      .map(breakIndex => ({
        xAxis: categories[breakIndex],
        lineStyle: {
          type: 'dashed',
          color: '#000000',
          width: 2
        },
        label: {
          show: false
        }
      }));

    if (markLines.length > 0) {
      series[0].markLine = {
        data: markLines,
        symbol: 'none',
        lineStyle: {
          type: 'dashed',
          color: '#000000',
          width: 2
        }
      };
    }

    // Adiciona grupos como markArea
    const markAreas = ruptureModel.groups.map((group) => {
      const [startIdx, endIdx] = group.startEndIndex.split('-').map(Number);
      if (startIdx >= categories.length || endIdx > categories.length) return null;

      const colors: Record<string, string> = {
        '1': 'rgba(0, 116, 192, 0.15)',
        '2': 'rgba(255, 87, 87, 0.15)',
        '3': 'rgba(76, 175, 80, 0.15)'
      };

      return [
        {
          xAxis: categories[startIdx],
          itemStyle: {
            color: colors[group.group_name] || 'rgba(128, 128, 128, 0.15)'
          }
        },
        {
          xAxis: categories[Math.min(endIdx - 1, categories.length - 1)]
        }
      ];
    }).filter(area => area !== null);

    if (markAreas.length > 0) {
      series[0].markArea = {
        data: markAreas,
        silent: true
      };
    }

    if(markAreas.length > 0 || markLines.length > 0) {
      this.generateLegendItems();
    }
  }

  generateLegendItems(): void {
    const items: Array<{ label: string; backgroundColor: string; borderColor: string; icon: string; hidden: boolean; ignoreHidden: boolean }> = [];
    const toggles = this.annotationToggles();

    if (this.selectedModel && this.rupturesData[this.selectedModel]) {
      items.push({
        label: 'Break Points',
        backgroundColor: '#000000',
        borderColor: '#000000',
        icon: 'trace-line',
        hidden: false,
        ignoreHidden: true
      });
      const uniqueGroups = [...new Set(this.rupturesData[this.selectedModel].groups.map(g => g.group_name))].sort();
      const colors: Record<string, string> = {
        '1': 'rgba(0, 116, 192, 0.15)',
        '2': 'rgba(255, 87, 87, 0.15)',
        '3': 'rgba(76, 175, 80, 0.15)'
      };
      uniqueGroups.forEach(groupName => {
        items.push({
          label: `Grupo ${groupName}`,
          backgroundColor: colors[groupName] || 'rgba(128, 128, 128, 0.15)',
          borderColor: colors[groupName] || 'rgba(128, 128, 128, 0.15)',
          icon: 'square',
          hidden: false,
          ignoreHidden: true
        });
      });
    }

    // if (toggles.thresholds) {
    //   items.push({ label: 'Linhas de referência (máx/mín/média)', backgroundColor: '#F39C12', borderColor: '#F39C12', icon: 'trace-line', hidden: false, ignoreHidden: true });
    // }
    if (toggles.nullPoints && this.hasNullData()) {
      items.push({
        label: 'Período sem dados',
        backgroundColor: 'rgba(120, 124, 132, 0.18)',
        borderColor: 'rgba(90, 96, 105, 0.42)',
        icon: 'square',
        hidden: false,
        ignoreHidden: true,
      });
    }
    // if (toggles.alertPoints) {
    //   items.push({ label: 'Valor máx/mín', backgroundColor: '#E74C3C', borderColor: '#3498DB', icon: 'pin', hidden: false, ignoreHidden: true });
    // }
    // if (toggles.treatmentMarkers) {
    //   items.push({ label: 'Dados tratados', backgroundColor: '#9B59B6', borderColor: '#9B59B6', icon: 'circle', hidden: false, ignoreHidden: true });
    // }
    // if (toggles.blockspots) {
    //   items.push({ label: 'Regiões destacadas', backgroundColor: 'rgba(231, 76, 60, 0.3)', borderColor: '#E74C3C', icon: 'square', hidden: false, ignoreHidden: true });
    // }

    this.legendItems.set(items);
  }

  private formatNumber(value: number, includeUnit?: boolean): string {
    const abs = Math.abs(value);
    const units = [
      { v: 1e12, s: 'T' },
      { v: 1e9, s: 'B' },
      { v: 1e6, s: 'M' },
      { v: 1e3, s: 'K' },
    ];

    let num = value;
    let suffix = '';
    for (const u of units) {
      if (abs >= u.v) {
        num = value / u.v;
        suffix = u.s;
        break;
      }
    }

    const formattedNum = num.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });

    if (includeUnit) return `${formattedNum}${suffix} ${this.temporalData?.unit || ''}`;
    else return `${formattedNum}${suffix}`;
  }

  getModelDescription(model: string): string {
    const ruptureModels = JSON.parse(JSON.stringify(ruptureModelsJson)).rupture_models;
    const modelDescription = ruptureModels.find(m => m.name === model)?.description;
    return modelDescription || 'Selecione um modelo para visualizar a descrição';
  }
}

